import { useState, useRef, useEffect, memo, type RefObject, type CSSProperties } from 'react';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@classify/react-core-components/Box';
import { useTheme, styled } from '@classify/react-core-components/styles';
import TocTogglerButton from './TocTogglerButton';
import TableOfContent from './TableOfContent';
import { useChapterContext } from '../_contexts/ChapterContext';
import useScrollAnimation, { ANIMATION_DURATION } from '../_hooks/useScrollAnimation';
export interface TocTogglerProps {
  onItemClick?: () => void;
  headerRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isInitialized?: boolean;
}
const SEPARATOR_WIDTH = 32;
const Separator = styled('div')(({
  theme: {
    breakpoints,
    palette,
    zIndex
  }
}) => ({
  display: 'none',
  [breakpoints.up('sm')]: {
    display: 'block',
    width: SEPARATOR_WIDTH,
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: zIndex.fab,
    borderRight: `1px solid ${palette.divider}`
  }
}));
const Container = styled('div')(({
  theme: {
    spacing,
    zIndex,
    breakpoints
  }
}) => ({
  display: 'inline-block',
  height: 48,
  zIndex: zIndex.fab,
  transition: `right ${ANIMATION_DURATION}ms`,
  bottom: spacing(15),
  position: 'fixed',
  right: 'var(--mobile-offset, 0px)',
  top: 'auto',
  [breakpoints.up('sm')]: {
    position: 'sticky',
    paddingLeft: spacing(4),
    paddingTop: spacing(5),
    right: 'auto',
    left: `${SEPARATOR_WIDTH}px`,
    top: 'var(--header-height, 70px)',
    bottom: 'auto'
  }
}));
const CustomDrawer = styled(Drawer, {
  shouldForwardProp: prop => prop !== 'hasVisibleHeader'
})<{
  hasVisibleHeader: boolean;
  open: boolean;
}>(({
  theme: {
    breakpoints,
    zIndex
  },
  hasVisibleHeader,
  open
}) => {
  const width = open ? '400px' : '0px';
  return {
    [breakpoints.up('sm')]: {
      width
    },
    '& .MuiDrawer-paper': {
      width: '100%',
      [breakpoints.up('sm')]: {
        width,
        maxWidth: 400,
        height: '100vh',
        position: hasVisibleHeader ? 'absolute' : 'fixed',
        zIndex: zIndex?.fab ?? 1050
      }
    }
  };
});
const TocToggler = memo(({
  onItemClick,
  headerRef,
  isOpen,
  setIsOpen,
  isInitialized = true
}: TocTogglerProps) => {
  const [hasVisibleHeader, setHasVisibleHeader] = useState(true);
  const tocRef = useRef<HTMLDivElement>(null);
  const {
    course
  } = useChapterContext();
  const {
    breakpoints
  } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('sm'));
  const mobileOpen = isInitialized ? isOpen : false;
  const desktopOpen = isInitialized ? isOpen : true;
  useEffect(function setOnClickAndScrollListeners() {
    const setHeaderVisibility = () => {
      if (!headerRef.current) return;
      const headerAndNavbarHeight = headerRef.current.clientHeight * 2;
      setHasVisibleHeader(window.scrollY < headerAndNavbarHeight);
    };
    window.addEventListener('scroll', setHeaderVisibility);
    return () => {
      window.removeEventListener('scroll', setHeaderVisibility);
    };
  }, [headerRef]);
  const [offset, setOffset] = useScrollAnimation({
    toggleVisibilityCallback: (isVisible: boolean) => {
      if (isVisible) {
        setOffset('0px');
      } else if (tocRef && tocRef.current) {
        setOffset(`-${tocRef.current.clientWidth}px`);
      }
    }
  });
  if (!course) return null;
  const {
    categoryColor
  } = course;
  const headerHeight = headerRef?.current?.clientHeight || 70;
  const handleItemClick = () => {
    if (!isDesktop) {
      setIsOpen(false);
    }
    if (onItemClick) {
      onItemClick();
    }
  };
  return <div>
        <Separator />
        <div ref={tocRef}>
          <Container style={{
        '--mobile-offset': offset,
        '--header-height': `${headerHeight}px`
      } as CSSProperties} data-testid="tocToggler">
            <TocTogglerButton onClick={() => {
          setIsOpen(true);
        }} categoryColor={categoryColor || '#000'} />
          </Container>
        </div>

        <Box sx={{
      display: {
        xs: 'none',
        sm: 'block'
      }
    }}>
          <CustomDrawer variant="persistent" anchor="left" open={desktopOpen} onClose={() => setIsOpen(false)} hasVisibleHeader={hasVisibleHeader}>
            <TableOfContent onClose={() => setIsOpen(false)} onClickItem={handleItemClick} color={categoryColor || '#000'} />
          </CustomDrawer>
        </Box>

        <Box sx={{
      display: {
        xs: 'block',
        sm: 'none'
      }
    }}>
          <CustomDrawer variant="persistent" anchor="right" open={mobileOpen} onClose={() => setIsOpen(false)} hasVisibleHeader={hasVisibleHeader}>
            <TableOfContent onClose={() => setIsOpen(false)} onClickItem={handleItemClick} color={categoryColor || '#000'} />
          </CustomDrawer>
        </Box>
      </div>;
});
export default TocToggler;